/**
 * @file Album Folder Upload actions.
 */

import getLogger from '../util/logger';
import { sentryCaptureMessage } from '../util/sentry';
import sleep from '../util/sleep';
import { ALBUM_PHOTO_STATUS } from '../constants/albums';

const log = getLogger('AlbumFolderAlbumFolderPhotosActions');

const DEFAULT_RETRY_ATTEMPT_SECONDS = 2;
const MAX_RETRY_ATTEMPTS = 5;

export const ALBUM_FOLDER_PHOTOS_DELETE_REQUEST =
  'albumFolder/PHOTOS_DELETE_REQUEST';
export const ALBUM_FOLDER_PHOTOS_DELETE_SUCCESS =
  'albumFolder/PHOTOS_DELETE_SUCCESS';
export const ALBUM_FOLDER_PHOTOS_DELETE_FAILURE =
  'albumFolder/PHOTOS_DELETE_FAILURE';
export const ALBUM_FOLDER_PHOTOS_DO_NOT_SHOW_DELETE_MODAL =
  'albumFolder/PHOTOS_NO_DELETE_MODAL';
export const ALBUM_FOLDER_PHOTOS_TOGGLE_MULTI_SELECT =
  'albumFolder/PHOTOS_TOGGLE_MULTI_SELECT';
export const ALBUM_FOLDER_PHOTOS_TOGGLE_PHOTO_ON_SELECT_LIST =
  'albumFolder/PHOTOS_TOGGLE_PHOTO_ON_SELECT_LIST';
export const ALBUM_FOLDER_PHOTOS_TOGGLE_TARGET_ALBUM =
  'albumFolder/PHOTOS_TOGGLE_TARGET_ALBUM';
export const ALBUM_FOLDER_PHOTOS_ASSIGN_REQUEST =
  'albumFolder/PHOTOS_ASSIGN_REQUEST';
export const ALBUM_FOLDER_PHOTOS_ASSIGN_SUCCESS =
  'albumFolder/PHOTOS_ASSIGN_SUCCESS';
export const ALBUM_FOLDER_PHOTOS_ASSIGN_FAILURE =
  'albumFolder/PHOTOS_ASSIGN_FAILURE';
export const ALBUM_FOLDER_PHOTOS_CLEAR_TARGET_ALBUMS =
  'albumFolder/ALBUM_FOLDER_PHOTOS_CLEAR_TARGET_ALBUMS';
export const ALBUM_FOLDER_PHOTOS_DO_NOT_SHOW_ASSIGN_CONFIRMATION =
  'albumFolder/PHOTOS_DO_NOT_SHOW_ASSIGN_MODAL';
export const ALBUM_FOLDER_PHOTOS_SELECT_ALL_FOR_REVIEW =
  'albumFolder/ALBUM_FOLDER_PHOTOS_SELECT_ALL_FOR_REVIEW';
export const ALBUM_FOLDER_PHOTOS_SELECT_ALL = 'albumFolder/PHOTOS_SELECT_ALL';
export const ALBUM_FOLDER_PHOTOS_DESELECT_ALL =
  'albumFolder/PHOTOS_DESELECT_ALL';
export const ALBUM_FOLDER_PHOTOS_SELECT_PHOTOS =
  'albumFolder/PHOTOS_SELECT_PHOTOS';
export const ALBUM_FOLDER_PHOTOS_DESELECT_PHOTOS =
  'albumFolder/PHOTOS_DESELECT_PHOTOS';
export const ALBUM_FOLDER_PHOTOS_RESET = 'albumFolder/PHOTOS_RESET';

const albumFolderPhotosDeleteRequest = () => {
  return { type: ALBUM_FOLDER_PHOTOS_DELETE_REQUEST };
};

const albumFolderPhotosDeleteSuccess = photoIds => {
  return { type: ALBUM_FOLDER_PHOTOS_DELETE_SUCCESS, photoIds };
};

const albumFolderPhotosDeleteFailure = photoId => {
  return { type: ALBUM_FOLDER_PHOTOS_DELETE_FAILURE, photoId };
};

const albumFolderPhotosAssignRequest = () => {
  return { type: ALBUM_FOLDER_PHOTOS_ASSIGN_REQUEST };
};

const albumFolderPhotosAssignSuccess = (
  photos,
  keepTargetAlbums,
  selectAll,
) => {
  return {
    type: ALBUM_FOLDER_PHOTOS_ASSIGN_SUCCESS,
    photos,
    keepTargetAlbums,
    selectAll,
  };
};

const albumFolderPhotosAssignFailure = (error, keepTargetAlbums) => {
  return {
    type: ALBUM_FOLDER_PHOTOS_ASSIGN_FAILURE,
    error,
    keepTargetAlbums,
  };
};

export const albumFolderDeleteFromAlbumFolderPhotos = (
  photoIds,
  albumFolderDeletePhotosMutation,
) => async (dispatch, getState) => {
  log.info(
    'albumFolderDeleteFromAlbumFolderPhotos() :: Photos will start being deleted... ',
  );
  dispatch(albumFolderPhotosDeleteRequest());

  const { selectAll } = getState().albumFolderPhotos;

  try {
    await albumFolderDeletePhotosMutation({ photoIds, selectAll });
    log.info('albumFolderDeleteFromAlbumFolderPhotos() :: Photos deleted... ');
    dispatch(albumFolderPhotosDeleteSuccess(photoIds));
  } catch (error) {
    log.error(
      'albumFolderDeleteFromAlbumFolderPhotos() :: Error deleting photos... ',
      error,
    );
    dispatch(albumFolderPhotosDeleteFailure(error));
  }
};

export const albumFolderPhotosDoNotShowDeleteModal = () => {
  return { type: ALBUM_FOLDER_PHOTOS_DO_NOT_SHOW_DELETE_MODAL };
};

export const toggleAlbumFolderPhotosMultiSelect = () => {
  return { type: ALBUM_FOLDER_PHOTOS_TOGGLE_MULTI_SELECT };
};

export const toggleAlbumFolderPhotosSelected = photoId => {
  return { type: ALBUM_FOLDER_PHOTOS_TOGGLE_PHOTO_ON_SELECT_LIST, photoId };
};

export const albumFolderSelectPhotos = photos => {
  return { type: ALBUM_FOLDER_PHOTOS_SELECT_PHOTOS, photos };
};

export const albumFolderDeselectPhotos = photos => {
  return { type: ALBUM_FOLDER_PHOTOS_DESELECT_PHOTOS, photos };
};

export const albumFolderSelectAll = () => {
  return { type: ALBUM_FOLDER_PHOTOS_SELECT_ALL };
};

export const albumFolderDeselectAll = () => {
  return { type: ALBUM_FOLDER_PHOTOS_DESELECT_ALL };
};

export const toggleAlbumFolderPhotosTargetAlbum = ({
  album,
  album_photo_group,
  albumId,
  photoGroupId,
  displayName,
  configuredTeamJerseys,
}) => {
  return {
    type: ALBUM_FOLDER_PHOTOS_TOGGLE_TARGET_ALBUM,
    target: {
      album,
      album_photo_group,
      albumId,
      photoGroupId,
      displayName,
      configuredTeamJerseys,
    },
  };
};

export const assignPhotosFromAlbumFolder = ({
  albumFolderPhotosAssignMutation,
  photoIds,
  keepTargetAlbums,
  errorLogParams,
  attemptedRetries = 0,
  albumPhotoStatus = ALBUM_PHOTO_STATUS.PUBLISHED,
}) => async (dispatch, getState) => {
  dispatch(albumFolderPhotosAssignRequest());
  const {
    targetAlbums,
    selectedPhotos,
    selectAll,
  } = getState().albumFolderPhotos;

  const photosToAssign = photoIds || selectedPhotos;
  log.info(
    'assignPhotosFromAlbumFolder() :: Photos will be assigned: ',
    photosToAssign,
  );
  const assignments = targetAlbums.map(targetAlbum => ({
    album: targetAlbum.albumId,
    album_photo_groups: targetAlbum.photoGroupId
      ? [targetAlbum.photoGroupId]
      : undefined,
  }));

  try {
    await albumFolderPhotosAssignMutation({
      albumFolderPhotos: photosToAssign,
      assignments,
      selectAll,
      albumPhotoStatus,
    });
    log.info('assignPhotosFromAlbumFolder() :: Photos have been assigned.');
    dispatch(
      albumFolderPhotosAssignSuccess(
        photosToAssign,
        keepTargetAlbums,
        selectAll,
      ),
    );

    return null;
  } catch (error) {
    log.error(
      'assignPhotosFromAlbumFolder() :: There was an error assigning photos: ',
      error,
    );

    if (attemptedRetries < MAX_RETRY_ATTEMPTS) {
      log.info(
        'assignPhotosFromAlbumFolder() :: Retrying photo assignment...',
        attemptedRetries,
      );

      await sleep(1000 * DEFAULT_RETRY_ATTEMPT_SECONDS);

      return dispatch(
        assignPhotosFromAlbumFolder({
          albumFolderPhotosAssignMutation,
          photoIds,
          keepTargetAlbums,
          errorLogParams,
          attemptedRetries: attemptedRetries + 1,
        }),
      );
    }

    sentryCaptureMessage(
      '[UCP - assignPhotosFromAlbumFolder] - Error found while photos were being assigned to albums.',
      {
        level: 'error',
        extra: {
          error,
          ...errorLogParams,
        },
      },
    );

    dispatch(albumFolderPhotosAssignFailure(error, keepTargetAlbums));

    return null;
  }
};

export const albumFolderPhotosDoNotShowAssignConfirmation = () => {
  return { type: ALBUM_FOLDER_PHOTOS_DO_NOT_SHOW_ASSIGN_CONFIRMATION };
};

export const albumFolderPhotosSelectAllForReview = () => {
  return { type: ALBUM_FOLDER_PHOTOS_SELECT_ALL_FOR_REVIEW };
};

export const albumFolderClearTargetAlbums = () => {
  return { type: ALBUM_FOLDER_PHOTOS_CLEAR_TARGET_ALBUMS };
};

export const albumFolderPhotosReset = albumFolderId => {
  return { type: ALBUM_FOLDER_PHOTOS_RESET, albumFolderId };
};
